import * as React from 'react';
import { Form, Button, Spinner, Modal, InputGroup, Table } from 'react-bootstrap';
import { AlertTriangle, Shield } from 'lucide-react';
import api from '../../../api';
import {
  getWebhookMigrationConfigUrl,
  updateWebhookMigrationConfigUrl,
  triggerWebhookUrlUpdateReminderUrl,
} from '../../../config';
import {
  IIntegrationAdoption,
  IIntegrationAdoptionOrg,
  IWebhookMigrationConfig,
} from '../analytics.types';
import useNotification from '../../../components/notification/useNotification';
import { EyeIcon, SearchIcon } from '../../../components/ui/icons';

interface IIntegrationAdoptionCardProps {
  integrations: IIntegrationAdoption[];
}

const WEBHOOK_MIGRATION_INTEGRATIONS = new Set(['Singlewire', 'Everbridge']);
const MIGRATION_HINT_DELAY_MS = 500;
const MIGRATION_HINT_VISIBLE_MS = 6000;

const toDateInputValue = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().split('T')[0];
};

const IntegrationAdoptionCard = ({
  integrations,
}: IIntegrationAdoptionCardProps): React.JSX.Element => {
  const [selectedIntegration, setSelectedIntegration] =
    React.useState<IIntegrationAdoption | null>(null);
  const [orgSearch, setOrgSearch] = React.useState<string>('');
  const [showMigrationModal, setShowMigrationModal] =
    React.useState<boolean>(false);
  const [showMigrationHint, setShowMigrationHint] =
    React.useState<boolean>(false);
  const [migrationConfig, setMigrationConfig] =
    React.useState<IWebhookMigrationConfig | null>(null);
  const [deadlineInput, setDeadlineInput] = React.useState<string>('');
  const [enabledInput, setEnabledInput] = React.useState<boolean>(true);
  const [isLoadingConfig, setIsLoadingConfig] = React.useState<boolean>(false);
  const [isSavingConfig, setIsSavingConfig] = React.useState<boolean>(false);
  const [isSendingReminders, setIsSendingReminders] =
    React.useState<boolean>(false);
  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const showEmptyState: boolean =
    !integrations.length ||
    integrations.every(
      (integration) =>
        integration.orgsUsing === 0 || integration.percentage === 0,
    );

  const supportsWebhookMigration = selectedIntegration
    ? WEBHOOK_MIGRATION_INTEGRATIONS.has(selectedIntegration.name)
    : false;

  const filteredOrganizations = React.useMemo(() => {
    const organizations = selectedIntegration?.organizations || [];
    const searchTerm = orgSearch.trim().toLowerCase();
    if (!searchTerm) {
      return organizations;
    }
    return organizations.filter((organization: IIntegrationAdoptionOrg) => {
      const name = (organization.orgName || '').toLowerCase();
      const email = (organization.adminEmail || '').toLowerCase();
      return name.includes(searchTerm) || email.includes(searchTerm);
    });
  }, [selectedIntegration, orgSearch]);

  React.useEffect(() => {
    const showTimer = window.setTimeout(() => {
      setShowMigrationHint(true);
    }, MIGRATION_HINT_DELAY_MS);

    const hideTimer = window.setTimeout(() => {
      setShowMigrationHint(false);
    }, MIGRATION_HINT_DELAY_MS + MIGRATION_HINT_VISIBLE_MS);

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);

  const fetchMigrationConfig = React.useCallback(async () => {
    setIsLoadingConfig(true);
    try {
      const response = await api.get(getWebhookMigrationConfigUrl);
      const config = response.data as IWebhookMigrationConfig;
      setMigrationConfig(config);
      setDeadlineInput(toDateInputValue(config.deadlineDate));
      setEnabledInput(!!config.enabled);
    } catch {
      showWarningNotification(
        'Webhook migration deadline settings could not be loaded.',
      );
    } finally {
      setIsLoadingConfig(false);
    }
  }, [showWarningNotification]);

  const openMigrationModal = () => {
    setShowMigrationHint(false);
    setShowMigrationModal(true);
    void fetchMigrationConfig();
  };

  const closeMigrationModal = () => {
    setShowMigrationModal(false);
  };

  const openOrganizationsModal = (integration: IIntegrationAdoption) => {
    setSelectedIntegration(integration);
    setOrgSearch('');
  };

  const closeOrganizationsModal = () => {
    setSelectedIntegration(null);
    setOrgSearch('');
  };

  const handleSaveDeadline = async () => {
    if (!deadlineInput) {
      showWarningNotification('Please select a deadline date.');
      return;
    }

    setIsSavingConfig(true);
    try {
      const response = await api.put(updateWebhookMigrationConfigUrl, {
        deadlineDate: new Date(`${deadlineInput}T23:59:59.000Z`).toISOString(),
        enabled: enabledInput,
      });
      const config = response.data as IWebhookMigrationConfig;
      setMigrationConfig(config);
      setDeadlineInput(toDateInputValue(config.deadlineDate));
      setEnabledInput(!!config.enabled);
      const emailsSent = Number(config.emailsSent || 0);
      showSuccessNotification(
        emailsSent > 0
          ? `Webhook migration deadline updated. ${emailsSent} notification email(s) sent.`
          : 'Webhook migration deadline updated.',
      );
    } catch {
      showWarningNotification(
        'Unable to update webhook migration deadline. Please try again.',
      );
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSendRemindersNow = async () => {
    setIsSendingReminders(true);
    try {
      const response = await api.post(triggerWebhookUrlUpdateReminderUrl, {});
      const result = response.data as {
        emailsSent?: number;
        suppressedCount?: number;
        skipped?: boolean;
        message?: string;
      };
      if (result.skipped) {
        showWarningNotification(
          result.message || 'Reminders were skipped by schedule rules.',
        );
      } else if (Number(result.suppressedCount || 0) > 0 && !Number(result.emailsSent || 0)) {
        showWarningNotification(
          `No emails delivered. ${result.suppressedCount} recipient(s) are suppressed by Azure Email. Remove them from the ACS suppression list or wait for expiry, then retry.`,
        );
      } else if (Number(result.suppressedCount || 0) > 0) {
        showWarningNotification(
          `Reminder emails sent: ${Number(result.emailsSent || 0)}. ${result.suppressedCount} recipient(s) suppressed by Azure Email.`,
        );
      } else {
        showSuccessNotification(
          `Reminder emails sent: ${Number(result.emailsSent || 0)}`,
        );
      }
      const configResponse = await api.get(getWebhookMigrationConfigUrl);
      setMigrationConfig(configResponse.data as IWebhookMigrationConfig);
    } catch {
      showWarningNotification(
        'Unable to send reminder emails. Please try again.',
      );
    } finally {
      setIsSendingReminders(false);
    }
  };

  return (
    <div className="analytics-card">
      <div className="card-title">
        <Shield size={20} strokeWidth={1.5} />
        <h2>Integration Adoption</h2>
        <div className="migration-deadline-trigger">
          {showMigrationHint && (
            <div className="migration-deadline-hint" role="status">
              <div className="migration-deadline-hint-body">
                <span className="migration-deadline-hint-badge">
                  <AlertTriangle size={14} strokeWidth={2.25} />
                </span>
                <div className="migration-deadline-hint-copy">
                  <strong>Check integration migration deadline</strong>
                  <span>
                    Open settings to review or update the webhook URL migration
                    deadline.
                  </span>
                </div>
              </div>
              <div className="migration-deadline-hint-progress" aria-hidden="true">
                <span />
              </div>
            </div>
          )}
          <button
            type="button"
            className={`migration-deadline-icon-btn${
              showMigrationHint ? ' is-hinting' : ''
            }`}
            onClick={openMigrationModal}
            aria-label="Open webhook migration deadline settings"
            title="Webhook migration deadline"
          >
            <AlertTriangle size={18} strokeWidth={2} />
          </button>
        </div>
      </div>

      {showEmptyState ? (
        <div className="analytics-empty-state">
          No integration adoption data available.
        </div>
      ) : (
        <div className="integration-list">
          {integrations.map((integration) => {
            const hasWebhookMigration = WEBHOOK_MIGRATION_INTEGRATIONS.has(
              integration.name,
            );

            return (
              <div
                key={integration.name}
                className="integration-block integration-block-clickable"
                role="button"
                tabIndex={0}
                onClick={() => openOrganizationsModal(integration)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openOrganizationsModal(integration);
                  }
                }}
              >
                <div className="integration-row">
                  <div className="integration-info">
                    <div className="integration-name-row">
                      <span className="integration-name">
                        {integration.name}
                      </span>
                      <span
                        className="integration-eye-icon"
                        title="View organizations"
                      >
                        <EyeIcon />
                      </span>
                    </div>
                    <span className="integration-count">
                      {integration.orgsUsing}/{integration.totalOrgs} orgs
                    </span>
                    {hasWebhookMigration && integration.webhookMigration && (
                      <span className="integration-migration-summary">
                        Updated {integration.webhookMigration.updatedCount} /
                        Pending {integration.webhookMigration.pendingCount}
                      </span>
                    )}
                  </div>
                  <div className="integration-bar-container">
                    <div
                      className="integration-bar"
                      style={{ width: `${integration.percentage}%` }}
                    />
                  </div>
                  <span className="integration-percentage">
                    {integration.percentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        show={showMigrationModal}
        onHide={closeMigrationModal}
        centered
        className="modal-container webhook-migration-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="viewdetails-heading">
              <h1>Webhook URL Migration Deadline</h1>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="webhook-migration-description">
            Reminder emails for Singlewire and Everbridge users run weekly until
            7 days remain, then daily. Saving or extending the deadline also
            sends notification emails immediately to pending users.
          </p>
          {isLoadingConfig ? (
            <div className="webhook-migration-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading deadline settings...</span>
            </div>
          ) : (
            <>
              <div className="webhook-migration-meta">
                <span>
                  Days remaining:{' '}
                  <strong>
                    {migrationConfig?.daysRemaining === null ||
                    migrationConfig?.daysRemaining === undefined
                      ? 'N/A'
                      : migrationConfig.daysRemaining}
                  </strong>
                </span>
                {migrationConfig?.migrationStats && (
                  <span>
                    Pending orgs: Singlewire{' '}
                    {migrationConfig.migrationStats.singlewire.pending},
                    Everbridge{' '}
                    {migrationConfig.migrationStats.everbridge.pending}
                  </span>
                )}
              </div>
              <div className="webhook-migration-controls">
                <Form.Group controlId="webhookMigrationDeadline">
                  <Form.Label>Deadline date</Form.Label>
                  <Form.Control
                    type="date"
                    value={deadlineInput}
                    onChange={(event) => setDeadlineInput(event.target.value)}
                  />
                </Form.Group>
                <Form.Check
                  type="switch"
                  id="webhookMigrationEnabled"
                  label="Reminders enabled"
                  checked={enabledInput}
                  onChange={(event) => setEnabledInput(event.target.checked)}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={closeMigrationModal}>
            Cancel
          </Button>
          <Button
            className="btn-cancel"
            onClick={handleSendRemindersNow}
            disabled={isLoadingConfig || isSavingConfig || isSendingReminders}
          >
            {isSendingReminders ? 'Sending...' : 'Send reminders now'}
          </Button>
          <Button
            className="btn-style"
            onClick={handleSaveDeadline}
            disabled={isLoadingConfig || isSavingConfig || isSendingReminders}
          >
            {isSavingConfig ? 'Saving...' : 'Save deadline'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!selectedIntegration}
        onHide={closeOrganizationsModal}
        centered
        className="modal-container integration-orgs-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="viewdetails-heading">
              <h1>
                {selectedIntegration?.name || 'Integration'} Organizations
              </h1>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="integration-org-search">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search by organization name or admin email"
                value={orgSearch}
                onChange={(event) => setOrgSearch(event.target.value)}
              />
              <InputGroup.Text>
                <SearchIcon />
              </InputGroup.Text>
            </InputGroup>
          </div>

          <div className="integration-org-meta">
            <span>
              Showing{' '}
              <span className="integration-org-count">
                {filteredOrganizations.length}
              </span>
              {(selectedIntegration?.organizations?.length || 0) !==
                filteredOrganizations.length && (
                <> of {selectedIntegration?.organizations?.length || 0}</>
              )}{' '}
              organizations
            </span>
          </div>

          <div className="integration-org-table-wrapper">
            <Table striped hover className="integration-org-table">
              <thead>
                <tr>
                  <th>Organization Name</th>
                  <th>Admin Email</th>
                  {supportsWebhookMigration && <th>Webhook Status</th>}
                </tr>
              </thead>
              <tbody>
                {!filteredOrganizations.length ? (
                  <tr>
                    <td
                      colSpan={supportsWebhookMigration ? 3 : 2}
                      className="integration-org-empty"
                    >
                      {orgSearch.trim()
                        ? 'No organizations match your search.'
                        : 'No organizations found.'}
                    </td>
                  </tr>
                ) : (
                  filteredOrganizations.map((organization) => (
                    <tr
                      key={`${selectedIntegration?.name}-${organization.orgId}`}
                    >
                      <td>{organization.orgName}</td>
                      <td>{organization.adminEmail || '—'}</td>
                      {supportsWebhookMigration && (
                        <td>
                          <span
                            className={`integration-org-badge ${
                              organization.webhookUrlUpdated
                                ? 'updated'
                                : 'pending'
                            }`}
                          >
                            {organization.webhookUrlUpdated
                              ? 'Updated'
                              : 'Pending'}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={closeOrganizationsModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default IntegrationAdoptionCard;
